<template>
  <div class="translation-manager bg-white rounded-lg shadow-lg">
    <!-- Header avec barre de recherche -->
    <TranslationManagerHeader
      :translation-keys="translationKeys"
      :translations="translations"
      :export-loading="exportLoading"
      :sync-loading="syncLoading"
      :save-all-loading="saveAllLoading"
      :pending-changes-count="pendingChangesCount"
      :search-query="searchQuery"
      :selected-context="selectedContext"
      :selected-language="selectedLanguage"
      :available-contexts="availableContexts"
      :available-languages="availableLanguages"
      @export="exportTranslationsToLocales"
      @sync="synchronizeWithFrontend"
      @save-all="saveAllPendingTranslations"
      @import="openImportModal"
      @update:search-query="updateSearchQuery"
      @update:selected-context="selectedContext = $event"
      @update:selected-language="selectedLanguage = $event"
      @reset-filters="resetFilters"
    />

    <!-- Section statistiques de synchronisation -->
    <div class="px-4 sm:px-6">
      <TranslationSyncStats v-if="syncStats" :sync-stats="syncStats" @close="syncStats = null" />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p class="mt-2 text-gray-600">{{ $t('translation.loading.text') }}</p>
    </div>

    <!-- Liste des traductions -->
    <TranslationKeyList
      v-else
      :loading="loading"
      :paginated-keys="paginatedKeys"
      :expanded-keys="expandedKeys"
      :available-languages="availableLanguages"
      :editable-values="editableValues"
      :get-translation="getTranslation"
      :has-unsaved-changes="hasUnsavedChanges"
      @toggle-expansion="toggleKeyExpansion"
      @update-translation="updateTranslation"
      @save-translation="saveTranslation"
    />

    <!-- Pagination -->
    <TranslationPagination
      v-if="totalPages > 1"
      :total-pages="totalPages"
      :current-page="currentPage"
      :items-per-page="itemsPerPage"
      :total="filteredKeys.length"
      @previous="currentPage--"
      @next="currentPage++"
    />

    <!-- Messages de succès/erreur -->
    <TranslationMessage :message="message" @close="message = null" />

    <!-- Modale Import depuis JSON -->
    <TranslationImportModal
      :show="showImportModal"
      :imported-data="importedData"
      :import-language-code="importLanguageCode"
      :comparison-results="comparisonResults"
      :selected-for-import="selectedForImport"
      :selected-different-count="selectedDifferentCount"
      :selected-new-count="selectedNewCount"
      :import-loading="importLoading"
      :available-languages="availableLanguages"
      @close="closeImportModal"
      @file-import="handleFileImport"
      @language-change="onImportLanguageChange"
      @toggle-selection="toggleImportSelection"
      @select-all-different="selectAllDifferent"
      @deselect-all-different="deselectAllDifferent"
      @select-all-new="selectAllNew"
      @deselect-all-new="deselectAllNew"
      @import="importSelectedTranslations"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslationManager } from '@/composables/useTranslationManager'
import { onMounted } from 'vue'
import TranslationImportModal from './translation/TranslationImportModal.vue'
import TranslationKeyList from './translation/TranslationKeyList.vue'
import TranslationManagerHeader from './translation/TranslationManagerHeader.vue'
import TranslationMessage from './translation/TranslationMessage.vue'
import TranslationPagination from './translation/TranslationPagination.vue'
import TranslationSyncStats from './translation/TranslationSyncStats.vue'

const {
  // State
  loading,
  exportLoading,
  syncLoading,
  saveAllLoading,
  importLoading,
  translationKeys,
  translations,
  expandedKeys,
  message,
  syncStats,
  searchQuery,
  selectedLanguage,
  selectedContext,
  currentPage,
  itemsPerPage,
  editableValues,
  availableLanguages,
  // Computed
  availableContexts,
  filteredKeys,
  totalPages,
  paginatedKeys,
  pendingChangesCount,
  selectedDifferentCount,
  selectedNewCount,
  // Methods
  loadTranslations,
  getTranslation,
  updateTranslation,
  hasUnsavedChanges,
  saveTranslation,
  saveAllPendingTranslations,
  toggleKeyExpansion,
  exportTranslationsToLocales,
  resetFilters,
  updateSearchQuery,
  synchronizeWithFrontend,
  openImportModal,
  closeImportModal,
  handleFileImport,
  onImportLanguageChange,
  toggleImportSelection,
  selectAllDifferent,
  deselectAllDifferent,
  selectAllNew,
  deselectAllNew,
  importSelectedTranslations,
  showImportModal,
  importedData,
  importLanguageCode,
  selectedForImport,
  comparisonResults,
} = useTranslationManager()

onMounted(() => {
  loadTranslations()
})
</script>

<style scoped>
.translation-manager {
  max-width: 100%;
}
</style>
